import { useState } from "react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/clientWeb/components/ui/radio-group"
import { Label } from "@/clientWeb/components/ui/label"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Plus, Mail, Users } from "lucide-react"
import { toast } from "@/clientWeb/components/ui/toast-utils"

interface License {
  id: string
  name: string
  type: "free" | "standard" | "pro" | "business"
  seats: number
  usedSeats: number
  expiryDate: string
  status: "active" | "expired" | "expiring"
  billingCycle: "monthly" | "yearly"
}

interface AddHostDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  license: License | null
}

export function AddHostDialog({ open, onOpenChange, license }: AddHostDialogProps) {
  const t = useTranslation()
  const [email, setEmail] = useState("")
  const [licenseType, setLicenseType] = useState("limit")
  const [availableLicenses, setAvailableLicenses] = useState("")
  const [emails, setEmails] = useState<string[]>([])

  const handleAddEmail = () => {
    if (email.trim() && !emails.includes(email.trim())) {
      setEmails(prev => [...prev, email.trim()])
      setEmail("")
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(prev => prev.filter(e => e !== emailToRemove))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddEmail()
    }
  }

  const handleSave = () => {
    if (emails.length > 0) {
      toast.success({
        title: t("licenses.hostsAdded"),
        description: t(emails.length === 1 ? "licenses.hostInvited" : "licenses.hostsInvited", { count: emails.length }),
      })
    }
    onOpenChange(false)
  }

  const handleCancel = () => {
    setEmail("")
    setEmails([])
    setLicenseType("limit")
    setAvailableLicenses("")
    onOpenChange(false)
  }

  if (!license) return null

  const availableSeats = license.seats - license.usedSeats

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
            {t("licenses.addHosts")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Description */}
          <p className="text-slate-600 text-sm dark:text-slate-400">
            {t("licenses.addHostsDesc")}
          </p>

          {/* Available License Info */}
          <div className="bg-slate-50 dark:bg-card/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <Users className="w-5 h-5" />
              <span className="font-medium">{t("licenses.availableLicense")}:</span>
              <span className="font-bold text-primary">{availableSeats}</span>
            </div>
          </div>

          {/* Email Input */}
          <div className="space-y-3">
            <span className="text-sm pb-2 font-medium text-slate-700 dark:text-slate-300">
              {t("licenses.emailAddress")}
            </span>
            <div className="flex gap-2 mt-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t("licenses.addEmail")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-10"
                />
              </div>
              <Button
                onClick={handleAddEmail}
                disabled={!email.trim()}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t("common.add")}
              </Button>
            </div>
          </div>

          {/* Added Emails */}
          {emails.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t("licenses.addedHosts")} ({emails.length})
              </Label>
              <div className="max-h-32 overflow-y-auto space-y-2 border border-stroke-100 dark:border-border dark:border-border rounded-lg p-3">
                {emails.map((emailItem, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-slate-50 dark:bg-card rounded-md px-3 py-2"
                  >
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {emailItem}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmail(emailItem)}
                      aria-label={t("common.remove")}
                      className="h-auto p-1 text-slate-400 hover:text-red-500"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* License Type Selection */}
          <div className="space-y-4">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t("licenses.licenseAssignment")}
            </span>
            <RadioGroup value={licenseType} onValueChange={setLicenseType} className="mt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="text-sm text-slate-600 dark:text-slate-400">
                  {t("licenses.allLicenses")}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="limit" id="limit" />
                <Label htmlFor="limit" className="text-sm text-slate-600 dark:text-slate-400">
                  {t("licenses.limitLicenses")}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Available License Input */}
          {licenseType === "limit" && (
            <div className="space-y-2">
              <Label htmlFor="available-license" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t("licenses.availableLicense")}
              </Label>
              <Input
                id="available-license"
                placeholder={t("licenses.availableLicense")}
                value={availableLicenses}
                onChange={(e) => setAvailableLicenses(e.target.value)}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-stroke-100 dark:border-border dark:border-border">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="px-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={emails.length === 0}
              className="bg-primary hover:bg-primary/90 text-white px-6"
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
